/**
 * The host's offer in the recorded room, as the confirmed prototype draws it: it lands in the corner of the picture
 * at its second and never pauses the talk; it folds to a tab that stays; on a small picture it opens under the
 * player; on a phone the dock carries it. The press that counts is the same everywhere: a link that opens with the
 * viewer's email on it, a code that copies, a file that saves, or a message that opens the chat.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { OfferJson } from "../../../shared/types";
import { useNoteStream } from "../../lib/noteStream";
import { fileHref, withEmail } from "../WatchOffer";
import { GIcon, type GIconName } from "./GIcon";
import { mmss } from "./talk";

const COPIED_MS = 2000;

/** The words on the tab and the dock: the button's own, or what the press does. */
export function offerPill(offer: OfferJson, host: string): string {
  if (offer.kind === "code") return offer.code ? `${offer.code} · Copy` : "Your code";
  if (offer.kind === "message") return `A note from ${host}`;
  return offer.label.trim() || (offer.kind === "call" ? "Book a call" : offer.kind === "buy" ? "Get started" : "Get the file");
}

/** Where a code is used, as a person reads it: "mayatorres.com/buyers". */
function bareUrl(url: string): string {
  return url.trim().replace(/^https?:\/\/(www\.)?/i, "").replace(/\/+$/, "");
}

function pillIcon(offer: OfferJson): GIconName {
  if (offer.kind === "code") return "copy";
  if (offer.kind === "download") return "down";
  if (offer.kind === "message") return "reply";
  return offer.kind === "buy" ? "ext" : "arrowr";
}

/** The one press, as a button or a link, carrying the classes the place it sits in asks for. */
export function OfferPress({
  offer,
  host,
  viewerEmail,
  className,
  children,
  onConvert,
  onChat,
}: {
  offer: OfferJson;
  host: string;
  viewerEmail: string;
  className: string;
  children?: ReactNode;
  onConvert: () => void;
  onChat: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const back = useRef(0);
  useEffect(() => () => window.clearTimeout(back.current), []);
  const inner = children ?? (
    <>
      {offerPill(offer, host)} <GIcon name={pillIcon(offer)} size={16} />
    </>
  );
  if (offer.kind === "code") {
    const copy = () => {
      onConvert();
      setCopied(true);
      window.clearTimeout(back.current);
      back.current = window.setTimeout(() => setCopied(false), COPIED_MS);
      try {
        void navigator.clipboard?.writeText(offer.code ?? "");
      } catch {
        /* a browser that keeps the clipboard to itself still shows the code */
      }
    };
    return (
      <button className={className} type="button" onClick={copy}>
        {copied ? (
          <>
            <GIcon name="check" size={16} /> Copied
          </>
        ) : (
          inner
        )}
      </button>
    );
  }
  if (offer.kind === "download" && offer.file) {
    return (
      <a className={className} href={fileHref(offer.file)} download target="_blank" rel="noreferrer" onClick={onConvert}>
        {inner}
      </a>
    );
  }
  if ((offer.kind === "call" || offer.kind === "buy") && offer.url) {
    return (
      <a className={className} href={withEmail(offer.url, viewerEmail)} target="_blank" rel="noopener noreferrer" onClick={onConvert}>
        {inner}
      </a>
    );
  }
  if (offer.kind === "message") {
    return (
      <button className={className} type="button" onClick={onChat}>
        {inner}
      </button>
    );
  }
  return null;
}

/** The host's short video on the offer: a poster that becomes the video when pressed. */
function OfferNote({ offer, host, onNote }: { offer: OfferJson; host: string; onNote: (state: "play" | "end") => void }): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLVideoElement>(null);
  const video = offer.video;
  const stream = useNoteStream(ref, open && video ? video.url : null);
  if (!video) return null;
  if (open) {
    return (
      <video
        ref={ref}
        className="oc-video"
        src={stream ? undefined : video.url}
        poster={video.poster ?? undefined}
        controls
        autoPlay
        playsInline
        aria-label={`A note from ${host}`}
        onEnded={() => onNote("end")}
      />
    );
  }
  return (
    <button
      type="button"
      className="oc-vnote"
      aria-label={`Play the note from ${host}, ${mmss(video.duration_sec)}`}
      onClick={() => {
        onNote("play");
        setOpen(true);
      }}
    >
      <span className="oc-vpic" style={video.poster ? { backgroundImage: `url("${video.poster}")` } : undefined}>
        <GIcon name="play" size={18} />
      </span>
      <span>
        <b>A note from <bdi>{host}</bdi></b>
        <small className="tnum">{mmss(video.duration_sec)}</small>
      </span>
    </button>
  );
}

/** What the card says and does: whose offer, what they said, the words, the file, the press. */
function OfferBody({
  offer,
  host,
  hostAvatar,
  said,
  viewerEmail,
  onConvert,
  onChat,
  onNote,
  end = false,
}: {
  offer: OfferJson;
  host: string;
  hostAvatar: string | null;
  said: string | null;
  viewerEmail: string;
  onConvert: () => void;
  onChat: () => void;
  onNote: (state: "play" | "end") => void;
  /** At the end of the talk the words are the heading and the line said is left out, as the prototype has it. */
  end?: boolean;
}): ReactNode {
  const words = offer.text?.trim() ?? "";
  const pressClass = offer.kind === "message" ? "btn btn-g" : "btn btn-p";
  return (
    <>
      <div className="oc-h">
        {hostAvatar ? <img className="av" src={hostAvatar} alt="" /> : null}
        <div>
          <b><bdi>{host}</bdi>’s offer</b>
          {said && !end ? <span>“{said}”</span> : null}
        </div>
      </div>
      {words ? end ? <h3>{words}</h3> : <p className="oc-words">{words}</p> : null}
      <OfferNote offer={offer} host={host} onNote={onNote} />
      {offer.kind === "download" && offer.file ? (
        <div className="oc-file">
          <span className="file-ic">PDF</span>
          <span>{offer.label.trim() || "The file"}</span>
        </div>
      ) : null}
      {offer.kind === "code" && offer.code ? (
        <>
          <div className="oc-code">
            <span>{offer.code}</span>
            <OfferPress offer={offer} host={host} viewerEmail={viewerEmail} className="btn btn-l btn-s" onConvert={onConvert} onChat={onChat}>
              <GIcon name="copy" size={16} /> Copy
            </OfferPress>
          </div>
          {offer.url ? <p className="oc-note">Use it at {bareUrl(offer.url)}.</p> : null}
        </>
      ) : (
        <div className="oc-act">
          <OfferPress offer={offer} host={host} viewerEmail={viewerEmail} className={pressClass} onConvert={onConvert} onChat={onChat}>
            {offer.kind === "message" ? (
              <>
                <GIcon name="reply" size={18} /> Write back to <bdi>{host}</bdi>
              </>
            ) : (
              <>
                {offerPill(offer, host)} <GIcon name={pillIcon(offer)} size={offer.kind === "download" ? 18 : 17} />
              </>
            )}
          </OfferPress>
        </div>
      )}
    </>
  );
}

export interface OfferProps {
  offer: OfferJson;
  host: string;
  hostAvatar: string | null;
  said: string | null;
  viewerEmail: string;
  onConvert: () => void;
  onChat: () => void;
  onNote: (state: "play" | "end") => void;
}

/** The card, open in the picture's corner (or under a small picture). */
export function OfferCard(props: OfferProps & { onFold: () => void }): ReactNode {
  return (
    <div className="rm-offer" role="region" aria-label={`${props.host}’s offer`}>
      <button className="ibtn oc-fold" type="button" aria-label={`Fold ${props.host}’s offer to a tab`} onClick={props.onFold}>
        <GIcon name="chu" size={18} />
      </button>
      <OfferBody {...props} />
    </div>
  );
}

/** The card folded to a tab: on a roomy picture the press and an opener, on a small one a single opener. */
export function OfferTab(props: OfferProps & { small: boolean; onOpen: () => void }): ReactNode {
  const { offer, host, hostAvatar, viewerEmail, onConvert, onChat, small, onOpen } = props;
  if (small) {
    return (
      <button className="rm-tab" type="button" aria-label={`Show ${host}’s offer`} onClick={onOpen}>
        {hostAvatar ? <img className="av" src={hostAvatar} alt="" /> : null}
        <span className="lbl"><bdi>{host}</bdi>’s offer</span>
      </button>
    );
  }
  return (
    <div className="rm-tab">
      {hostAvatar ? <img className="av" src={hostAvatar} alt="" /> : null}
      <OfferPress offer={offer} host={host} viewerEmail={viewerEmail} className="go" onConvert={onConvert} onChat={onChat} />
      <button className="ibtn" type="button" aria-label={`Open ${host}’s offer`} onClick={onOpen}>
        <GIcon name="chd" size={18} />
      </button>
    </div>
  );
}

/** The offer on the phone's dock. */
export function OfferDock(props: OfferProps): ReactNode {
  const { offer, host, viewerEmail, onConvert, onChat } = props;
  return (
    <OfferPress offer={offer} host={host} viewerEmail={viewerEmail} className="dk-offer" onConvert={onConvert} onChat={onChat}>
      <GIcon name="gift" size={18} />
      <span>{offerPill(offer, host)}</span>
    </OfferPress>
  );
}

/** The end of the talk: the offer again, larger, for the end card and the part under the player. */
export function OfferEnd(props: OfferProps): ReactNode {
  return (
    <div className="end-offer">
      <OfferBody {...props} end />
    </div>
  );
}
